'use client'

import { useEffect, useEffectEvent, useMemo, useState } from 'react'

export type VoiceRecorderState = 'idle' | 'recording' | 'denied'

export interface VoiceRecording {
  blob: Blob
  mimeType: string
  /** Lo que duró la grabación, en segundos. */
  durationSeconds: number
}

export interface UseVoiceRecorderOptions {
  maxSeconds: number
  /** Una nota más corta se descarta como si se cancelara (`stop()` devuelve `null`). */
  minSeconds?: number
  /** Al cortar por tiempo, la grabación se entrega aquí (nadie llama a `stop()`). */
  onLimit?: (recording: VoiceRecording) => void
}

export interface VoiceRecorder {
  state: VoiceRecorderState
  start(): Promise<void>
  stop(): Promise<VoiceRecording | null>
  cancel(): void
}

interface ActiveRecording {
  recorder: MediaRecorder
  stream: MediaStream
  chunks: Blob[]
  mimeType: string
  startedAt: number
  timer: ReturnType<typeof setTimeout> | null
}

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}
/** Voz de un niño: de sobra para transcribir y deja 30 s muy por debajo de 1 MB. */
const AUDIO_BITS_PER_SECOND = 64_000

function isRecordingSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)
}

/** `audio/webm` (Chrome, Firefox) o, si no, `audio/mp4` (Safari): los dos que acepta la API. */
function pickMimeType(): string {
  return MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
}

function releaseStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => track.stop())
}

function requestStream(): Promise<MediaStream | null> {
  return navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS }).catch(() => null)
}

/** Monta el grabador sobre el stream; si el navegador no puede, suelta el stream y devuelve `null`. */
function createRecording(stream: MediaStream): ActiveRecording | null {
  const mimeType = pickMimeType()
  let recorder: MediaRecorder
  try {
    recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND })
  } catch {
    releaseStream(stream)
    return null
  }
  const active: ActiveRecording = { recorder, stream, chunks: [], mimeType, startedAt: Date.now(), timer: null }
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) active.chunks.push(event.data)
  }
  return active
}

function clearLimitTimer(active: ActiveRecording): void {
  if (active.timer) clearTimeout(active.timer)
  active.timer = null
}

/** Para el grabador y resuelve con lo grabado (o `null` si no hay audio o dura menos de `minSeconds`). */
function finish(active: ActiveRecording, minSeconds: number): Promise<VoiceRecording | null> {
  const elapsedSeconds = (Date.now() - active.startedAt) / 1000
  if (elapsedSeconds < minSeconds) {
    discard(active)
    return Promise.resolve(null)
  }
  clearLimitTimer(active)
  const durationSeconds = Math.round(elapsedSeconds)
  return new Promise((resolve) => {
    const done = () => {
      releaseStream(active.stream)
      const blob = new Blob(active.chunks, { type: active.mimeType })
      resolve(blob.size > 0 ? { blob, mimeType: active.mimeType, durationSeconds } : null)
    }
    if (active.recorder.state === 'inactive') {
      done()
      return
    }
    active.recorder.onstop = done
    active.recorder.stop()
  })
}

function discard(active: ActiveRecording): void {
  clearLimitTimer(active)
  active.recorder.onstop = null
  if (active.recorder.state !== 'inactive') active.recorder.stop()
  releaseStream(active.stream)
}

/**
 * Nota de voz sobre `MediaRecorder`: mantener pulsado → `start()`, soltar →
 * `stop()`. Solo corta por su cuenta al llegar a `maxSeconds`. Sin
 * micrófono (denegado o no soportado) pasa a `denied` y la pantalla ofrece el texto.
 */
export function useVoiceRecorder({ maxSeconds, minSeconds = 0, onLimit }: UseVoiceRecorderOptions): VoiceRecorder {
  const [state, setState] = useState<VoiceRecorderState>('idle')
  const session = useMemo(
    () => new RecordingSession({ maxSeconds, minSeconds, setState }),
    [maxSeconds, minSeconds]
  )

  // Always the caller's latest `onLimit`, registered once per session: an inline callback no longer re-runs the effect.
  const onLimitEvent = useEffectEvent((recording: VoiceRecording) => onLimit?.(recording))
  useEffect(() => {
    session.setOnLimit((recording) => onLimitEvent(recording))
  }, [session])

  useEffect(() => () => session.cancel(), [session])

  return useMemo(
    () => ({
      state,
      start: () => session.start(),
      stop: () => session.stop(),
      cancel: () => session.cancel(),
    }),
    [state, session]
  )
}

interface RecordingSessionOptions {
  maxSeconds: number
  minSeconds: number
  setState: (state: VoiceRecorderState) => void
}

/**
 * Una grabación como mucho a la vez. `starting`/`aborted` cubren la espera
 * del permiso: un segundo `start()` no abre otro stream y un `stop()`/
 * `cancel()` durante la espera suelta el stream en cuanto llega.
 */
class RecordingSession {
  private active: ActiveRecording | null = null
  private starting = false
  private aborted = false
  private onLimit: ((recording: VoiceRecording) => void) | undefined

  constructor(private readonly options: RecordingSessionOptions) {}

  setOnLimit(onLimit: ((recording: VoiceRecording) => void) | undefined): void {
    this.onLimit = onLimit
  }

  async start(): Promise<void> {
    if (this.active || this.starting) return
    if (!isRecordingSupported()) return this.options.setState('denied')
    this.starting = true
    this.aborted = false
    try {
      await this.acquire()
    } finally {
      this.starting = false
    }
  }

  async stop(): Promise<VoiceRecording | null> {
    const active = this.take()
    if (!active) return null
    this.options.setState('idle')
    return finish(active, this.options.minSeconds)
  }

  cancel(): void {
    const active = this.take()
    if (!active) return
    discard(active)
    this.options.setState('idle')
  }

  private async acquire(): Promise<void> {
    const stream = await requestStream()
    if (!stream) return this.options.setState('denied')
    if (this.aborted) return releaseStream(stream)
    const active = createRecording(stream)
    if (!active) return this.options.setState('denied')
    this.begin(active)
  }

  private begin(active: ActiveRecording): void {
    active.timer = setTimeout(() => void this.stopAtLimit(), this.options.maxSeconds * 1000)
    this.active = active
    active.recorder.start()
    this.options.setState('recording')
  }

  private async stopAtLimit(): Promise<void> {
    const recording = await this.stop()
    if (recording) this.onLimit?.(recording)
  }

  /** Saca la grabación en curso; si aún se espera el micrófono, la deja abortada. */
  private take(): ActiveRecording | null {
    if (this.starting) this.aborted = true
    const active = this.active
    this.active = null
    return active
  }
}
